import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AdminRole } from '@/constants/adminRole'
import { toast } from '@/lib/toast'
import { api } from '@/api'
import { useState } from 'react'

type CreatedAdmin = {
  id: number
  name: string
  username: string
}

type Props = {
  onCreated: (admin: CreatedAdmin) => void
}

export function PartnerAdminCreateDialog({ onCreated }: Props) {
  const [open, setOpen] = useState(false)
  const [username, setUsername] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setUsername('')
    setName('')
    setPassword('')
  }

  const handleCreate = async () => {
    const trimmedUsername = username.trim()
    const trimmedName = name.trim()

    if (!trimmedUsername) {
      toast.error('아이디를 입력해주세요.')
      return
    }
    if (!trimmedName) {
      toast.error('이름을 입력해주세요.')
      return
    }
    if (!password) {
      toast.error('비밀번호를 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    try {
      const created = await api().admin.createAdmin({
        username: trimmedUsername,
        name: trimmedName,
        password,
        passwordConfirm: password,
        role: AdminRole.PARTNER_ADMIN,
      })

      toast.success('파트너 관리자 계정을 생성했습니다.')
      onCreated({
        id: created.id,
        name: created.name,
        username: created.username,
      })
      resetForm()
      setOpen(false)
    } catch (error: unknown) {
      const message =
        (error as { error?: { message?: string } })?.error?.message ??
        '계정 생성에 실패했습니다.'
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          resetForm()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          파트너 관리자 새로 만들기
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>파트너 관리자 생성</DialogTitle>
        </DialogHeader>
        <div className="grid gap-12 py-8">
          <div className="grid gap-6">
            <Label htmlFor="partner-admin-username">아이디</Label>
            <Input
              id="partner-admin-username"
              value={username}
              autoComplete="off"
              placeholder="로그인 아이디"
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="grid gap-6">
            <Label htmlFor="partner-admin-name">이름</Label>
            <Input
              id="partner-admin-name"
              value={name}
              placeholder="표시 이름"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-6">
            <Label htmlFor="partner-admin-password">비밀번호</Label>
            <Input
              id="partner-admin-password"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder="초기 비밀번호"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isSubmitting}
          >
            취소
          </Button>
          <Button type="button" onClick={() => void handleCreate()} disabled={isSubmitting}>
            {isSubmitting ? '생성 중…' : '생성'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
